import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Alert, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";

import CargoTankAssignmentDialog from "view/voyages/components/cargotanks/CargoTankAssignmentDialog";

import { StatusChangerCargoTanksDocument, StatusChangerCargoTanksQuery } from "__generated__/view/statuses/graphql/queries.generated";

import "../../styling/styling.scss";

type StatusChangerRequiredTanksFieldProps = {
	cargoId: number;
	onChange: (valid: boolean) => void;
};

const StatusChangerRequiredTanksField = ({ cargoId, onChange }: StatusChangerRequiredTanksFieldProps) => {

	const [ dialogOpen, setDialogOpen ] = useState(false);

	const { data, refetch, loading } = useQuery<StatusChangerCargoTanksQuery>(StatusChangerCargoTanksDocument, {
		variables: {
			id: cargoId,
		},
	});

	const cargo = data?.cargo;
	const voyage = cargo?.voyage;

	// only applicable if the ship actually has tanks
	const applicable = !loading && (voyage?.ship.tanks.length ?? 0) > 0;
	const checked = (cargo?.tanks.length ?? 0) > 0;

	useEffect(() => {
		onChange(!loading && (!applicable || checked));
	}, [ loading, applicable, checked ]);

	if (!applicable || !voyage) {
		return (
			<React.Fragment />
		);
	}

	return (
		<div className="cargo-tank-checks">
			{!checked && (
				<Alert
					severity="warning"
					action={
						<Tooltip title={intl.formatMessage({ id: "UPDATES_REFRESH" })} placement="top">
							<IconButton onClick={() => void refetch()} size="small">
								<RefreshIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					}>
					{intl.formatMessage({ id: "TANKS_MUST_BE_ASSIGNED" })}
				</Alert>
			)}

			<div className="cam-checks">
				<div className="cam-check">
					<span className="description">
						{cargo?.cargoNumber || cargo?.containerNumber || cargo?.product?.name}
					</span>

					<span className="actions">
						<IconButton size="small" disabled>
							{checked ? (
								<CheckIcon fontSize="small" color="success" />
							) : (
								<ClearIcon fontSize="small" color="error" />
							)}
						</IconButton>

						{!checked && (
							<React.Fragment>
								<Tooltip title={intl.formatMessage({ id: "TANK_ASSIGNMENT" })} placement="top">
									<IconButton onClick={() => setDialogOpen(true)} size="small">
										<EditIcon fontSize="small" />
									</IconButton>
								</Tooltip>

								{dialogOpen && (
									<CargoTankAssignmentDialog
										open={dialogOpen}
										voyageId={voyage.id}
										shipId={voyage.ship.id}
										cargo={cargo!}
										onClose={() => setDialogOpen(false)}
										refetch={() => void refetch()} />
								)}
							</React.Fragment>
						)}
					</span>
				</div>
			</div>
		</div>
	);
};

export default StatusChangerRequiredTanksField;
